<template>
  <div :class="$style.surface" test-id="drag-ghost">
    <div v-if="stackRows.length" :class="$style.stack">
      <div v-for="(row, index) in stackRows" :key="index" :class="$style.stackRow">
        <SCASvgIcon :icon="row.icon ?? FALLBACK_ICON" :color="row.iconColor" size="16px" />
        <span :class="$style.label">{{ row.label }}</span>
      </div>
      <div v-if="hiddenStackCount > 0" :class="[$style.stackRow, $style.stackMore]">
        {{ $t("common.label.moreDragged", { count: hiddenStackCount }) }}
      </div>
    </div>
    <div v-else :class="$style.card">
      <SCASvgIcon v-if="icon" :icon="icon" :color="item?.iconColor" size="16px" />
      <span v-if="item" :class="$style.label">{{ item.label }}</span>
      <span v-if="totalCount > 1" :class="$style.badge">{{ totalCount }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { $t } from "../../i18n";
import SCASvgIcon from "../SCASvgIcon.vue";
import type { DragGhostItem } from "./types";

/**
 * Stands in whenever there's no icon to show — the caller had nothing to name at all, or it's a
 * plain reorder-list item with no icon of its own. Doubles as the reorder handle glyph, so a bare
 * `{ label }` ghost still reads as "drag to reorder" rather than a blank card.
 */
const FALLBACK_ICON = "phosphor/dots-six-vertical";

const STACK_LIMIT = 5;

const props = defineProps<{
  item?: DragGhostItem;
  totalCount: number;
  stack?: readonly DragGhostItem[];
}>();

const icon = computed(() => props.item?.icon ?? FALLBACK_ICON);
const stackRows = computed(() => (props.stack?.length ? props.stack.slice(0, STACK_LIMIT) : []));
const hiddenStackCount = computed(() => (stackRows.value.length ? props.stack!.length - stackRows.value.length : 0));

defineOptions({
  name: "SCADragGhost",
});
</script>

<style lang="scss" module>
@use "../../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;

.surface {
  display: inline-block;
  max-width: 260px;
}

.card {
  border: 1px solid var(--color-coolGreyA-p5);
  border-radius: var(--border-radius-large);
  background: var(--color-interaction-background-modeless);
  box-shadow:
    0px 2px 2px -1px rgba(27, 35, 44, 0.04),
    0px 8px 16px -2px rgba(27, 36, 44, 0.12);
  display: flex;
  align-items: center;
  gap: var(--space-component-gap-small);
  padding: var(--space-component-padding-small) var(--space-component-padding-medium);
  color: var(--color-content-primary);
}

.stack {
  display: flex;
  flex-direction: column;
  width: 280px;
  padding: var(--space-component-padding-medium) var(--space-component-padding-xSmall);
  border: 1px solid var(--color-coolGreyA-p5);
  border-radius: var(--border-radius-large);
  background: var(--color-action-primary-subtle-selected);
  box-shadow:
    0px 2px 2px -1px rgba(27, 35, 44, 0.04),
    0px 8px 16px -2px rgba(27, 36, 44, 0.12);
  overflow: hidden;

  &:has(> :only-child) {
    padding: 0 var(--space-component-padding-xSmall);
  }
}

.stackRow {
  display: flex;
  align-items: center;
  gap: var(--space-component-gap-small);
  height: var(--size-medium);
  padding: 0 var(--space-component-padding-small);
  color: var(--color-content-primary);

  & + & {
    border-top: 1px solid transparent;
  }
}

.stackMore {
  @include FigmaTypoMixins.typo-body-strong-xsmall;
  padding-left: calc(var(--space-component-padding-small) + 16px + var(--space-component-gap-small));
  height: var(--size-2xSmall);
  color: var(--color-action-primary-active);
}

.label {
  @include FigmaTypoMixins.typo-body-small;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.badge {
  @include FigmaTypoMixins.typo-body-strong-xsmall;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 var(--space-component-padding-xSmall);
  border-radius: var(--border-radius-large);
  background: var(--color-action-primary-normal);
  color: var(--color-content-primary-inverted);
}
</style>
